"use client";

import { useEffect, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  label: string;
  value: number;
  suffix: string;
  descriptor: string;
};
const metrics: Metric[] = [{
  label: "DAILY OUTPUT",
  value: 15,
  suffix: "TONS",
  descriptor: "High-capacity extrusion lines"
}, {
  label: "CUSTOM MOLDS",
  value: 85,
  suffix: "UNITS",
  descriptor: "Precision tooling for packaging"
}, {
  label: "GLOBAL CLIENTS",
  value: 450,
  suffix: "PARTNERS",
  descriptor: "Trusted wholesale distribution"
}, {
  label: "LEAD TIME",
  value: 4,
  suffix: "WKS",
  descriptor: "Bulk order fulfillment cycle"
}];
function CountUp({
  end,
  isVisible
}: {
  end: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1800;
    const startTime = performance.now();
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [end, isVisible]);
  return <>{count.toLocaleString()}</>;
}
function getCurrentTimestamp(): string {
  const now = new Date();
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${months[now.getMonth()]} ${now.getFullYear()}`;
}
export default function ManufacturingScaleStats() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    once: true
  });
  const timestamp = getCurrentTimestamp();
  return <section data-section-id="2517" id="manufacturing-scale-stats" className="relative bg-background text-foreground overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 1px, transparent 1px, transparent 6px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 1px, transparent 1px, transparent 6px)"
    }} />

      <div ref={ref} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:py-[180px]">
        {}
        <div className="flex items-center justify-between mb-12 md:mb-20">
          <span className="font-mono text-xs sm:text-sm uppercase tracking-widest text-muted-foreground">
            § 04 / INDUSTRIAL CAPACITY
          </span>
          <span className="font-mono text-xs sm:text-sm uppercase tracking-widest text-muted-foreground">
            UPDATED: {timestamp}
          </span>
        </div>

        <Separator className="mb-12 md:mb-16 bg-border" />

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 lg:gap-y-0 relative">
          {metrics.map((metric, i) => <div data-index={i} key={metric.label} className={`relative px-4 lg:px-8 ${i > 0 ? "lg:border-l lg:border-border" : ""} ${i === 1 || i === 3 ? "sm:border-l sm:border-border lg:border-l" : ""}`} style={{
          transition: "opacity 700ms ease, transform 700ms ease",
          transitionDelay: `${i * 120}ms`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
              {}
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                {metric.label}
              </div>

              {}
              <div className="flex gap-1 mb-3" aria-hidden="true">
                <span className="block w-px h-2 bg-primary" />
                <span className="block w-px h-2 bg-primary" />
                <span className="block w-px h-2 bg-primary" />
                <span className="block w-px h-2 bg-primary/60" />
                <span className="block w-px h-2 bg-primary/40" />
              </div>

              {}
              <div className="flex items-baseline gap-2">
                <span className="font-default font-bold leading-none text-foreground tabular-nums tracking-tight" style={{
              fontSize: "clamp(4rem, 8vw, 8rem)"
            }}>
                  <CountUp end={metric.value} isVisible={isVisible} />
                </span>
                <span className="font-default font-bold leading-none text-primary tracking-tight" style={{
              fontSize: "clamp(2rem, 4vw, 4rem)"
            }}>
                  {metric.suffix}
                </span>
              </div>

              {}
              <p className="mt-6 text-sm text-muted-foreground max-w-[200px]">
                {metric.descriptor}
              </p>
            </div>)}
        </div>

        <Separator className="mt-12 md:mt-16 bg-border" />

        {}
        <div className="mt-8 flex items-center justify-between">
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground">
            DATA / VERIFIED PRODUCTION
          </span>
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground">
            AKYASCO / INDUSTRIAL PLASTICS
          </span>
        </div>
      </div>
    </section>;
}